if (!customElements.get('sevenk-league-carousel')) {
  customElements.define(
    'sevenk-league-carousel',
    class SevenKLeagueCarousel extends HTMLElement {
      connectedCallback() {
        this.track = this.querySelector('[data-sevenk-league-track]');
        this.viewport = this.querySelector('[data-sevenk-league-viewport]');
        this.firstGroup = this.querySelector('[data-sevenk-league-group]');
        if (!this.track || !this.viewport || !this.firstGroup) return;

        this.position = 0;
        this.speed = 0.027;
        this.velocity = 0;
        this.dragging = false;
        this.paused = false;
        this.moved = false;
        this.lastPointerX = 0;
        this.lastPointerTime = 0;
        this.pointerStartX = 0;
        this.pointerStartY = 0;
        this.gestureDirection = null;
        this.capturedPointerId = null;
        this.lastFrameTime = performance.now();
        this.reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

        this.viewport.addEventListener('pointerdown', this.onPointerDown.bind(this));
        this.viewport.addEventListener('pointermove', this.onPointerMove.bind(this));
        this.viewport.addEventListener('pointerup', this.onPointerUp.bind(this));
        this.viewport.addEventListener('pointercancel', this.onPointerUp.bind(this));
        this.viewport.addEventListener('mouseenter', () => (this.paused = true));
        this.viewport.addEventListener('mouseleave', () => {
          if (!this.dragging) this.paused = false;
        });
        this.viewport.addEventListener('focusin', () => (this.paused = true));
        this.viewport.addEventListener('focusout', () => (this.paused = false));
        this.viewport.addEventListener('keydown', this.onKeyDown.bind(this));
        this.viewport.addEventListener(
          'click',
          (event) => {
            if (this.moved) {
              event.preventDefault();
              event.stopPropagation();
              this.moved = false;
            }
          },
          true
        );

        this.resizeObserver = new ResizeObserver(() => this.normalize());
        this.resizeObserver.observe(this.firstGroup);
        this.animationFrame = requestAnimationFrame(this.animate.bind(this));
      }

      disconnectedCallback() {
        cancelAnimationFrame(this.animationFrame);
        this.resizeObserver?.disconnect();
      }

      get cycleWidth() {
        const secondGroup = this.track?.querySelectorAll('[data-sevenk-league-group]')[1];
        return secondGroup ? secondGroup.offsetLeft : this.firstGroup?.offsetWidth || 0;
      }

      normalize() {
        const cycle = this.cycleWidth;
        if (!cycle) return;
        this.position = ((this.position % cycle) + cycle) % cycle;
        this.render();
      }

      render() {
        this.track.style.transform = `translate3d(${-this.position}px, 0, 0)`;
      }

      animate(timestamp) {
        const elapsed = Math.min(timestamp - this.lastFrameTime, 34);
        this.lastFrameTime = timestamp;

        if (!this.dragging && !this.reducedMotion.matches) {
          if (Math.abs(this.velocity) > 0.01) {
            this.position += this.velocity * elapsed;
            this.velocity *= Math.pow(0.94, elapsed / 16.67);
          } else if (!this.paused) {
            this.velocity = 0;
            this.position += this.speed * elapsed;
          }
          this.normalize();
        }
        this.animationFrame = requestAnimationFrame(this.animate.bind(this));
      }

      onPointerDown(event) {
        if (event.pointerType === 'mouse' && event.button !== 0) return;
        this.dragging = true;
        this.paused = true;
        this.moved = false;
        this.velocity = 0;
        this.pointerStartX = event.clientX;
        this.pointerStartY = event.clientY;
        this.lastPointerX = event.clientX;
        this.lastPointerTime = event.timeStamp;
        this.gestureDirection = null;
        this.viewport.classList.add('is-dragging');
      }

      onPointerMove(event) {
        if (!this.dragging) return;

        const samples = event.getCoalescedEvents ? event.getCoalescedEvents() : [event];
        for (const sample of samples) {
          if (!this.gestureDirection) {
            const totalX = sample.clientX - this.pointerStartX;
            const totalY = sample.clientY - this.pointerStartY;
            if (Math.max(Math.abs(totalX), Math.abs(totalY)) < 5) continue;
            this.gestureDirection = Math.abs(totalX) > Math.abs(totalY) ? 'horizontal' : 'vertical';
          }

          if (this.gestureDirection !== 'horizontal') continue;
          if (this.capturedPointerId === null) {
            this.viewport.setPointerCapture(event.pointerId);
            this.capturedPointerId = event.pointerId;
          }
          const delta = sample.clientX - this.lastPointerX;
          const elapsed = Math.max(sample.timeStamp - this.lastPointerTime, 1);
          this.position -= delta;
          this.velocity = this.velocity * 0.7 + (-delta / elapsed) * 0.3;
          this.lastPointerX = sample.clientX;
          this.lastPointerTime = sample.timeStamp;
          if (Math.abs(sample.clientX - this.pointerStartX) > 5) this.moved = true;
        }

        if (this.gestureDirection === 'horizontal') this.normalize();
      }

      onPointerUp(event) {
        if (!this.dragging) return;
        this.dragging = false;
        this.paused = false;
        if (this.gestureDirection !== 'horizontal') this.velocity = 0;
        this.viewport.classList.remove('is-dragging');
        if (this.capturedPointerId !== null && this.viewport.hasPointerCapture(this.capturedPointerId)) {
          this.viewport.releasePointerCapture(this.capturedPointerId);
        }
        this.capturedPointerId = null;
        window.setTimeout(() => (this.moved = false), 80);
      }

      onKeyDown(event) {
        if (event.key !== 'ArrowLeft' && event.key !== 'ArrowRight') return;
        event.preventDefault();
        this.position += event.key === 'ArrowRight' ? 180 : -180;
        this.normalize();
      }
    }
  );
}
